import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Use relative URLs in production, localhost in development
const API_BASE = process.env.NODE_ENV === "production" ? "" : "http://localhost:8081";

interface MonitoringDashboardProps {
  tenantId?: string;
}

interface HealthStatus {
  status: 'healthy' | 'warning' | 'degraded' | 'critical';
  metrics: {
    total_executions: number;
    successful_executions: number;
    success_rate_pct: number;
    error_rate_pct: number;
    avg_latency_ms: number;
    p95_latency_ms: number;
    total_cost_usd: number;
  };
  agents: Array<{
    agent_id: string;
    executions: number;
    success_rate_pct: number;
    avg_latency_ms: number;
    total_cost_usd: number;
  }>;
}

interface Alert {
  alert_id: number;
  rule_name: string;
  severity: string;
  metric_type: string;
  current_value: number;
  threshold_value: number;
  message: string;
  triggered_at: string;
  resolved_at?: string;
}

interface AlertRule {
  rule_id: number;
  rule_name: string;
  enabled: boolean;
  metric_type: string;
  threshold_value: number;
  threshold_operator: string;
  severity: string;
  email_recipients: string[];
}

interface SLATarget {
  sla_id: number;
  sla_name: string;
  tenant_id: string;
  agent_id?: string;
  uptime_percentage?: number;
  max_latency_p95_ms?: number;
  min_success_rate_pct?: number;
  max_cost_per_day_usd?: number;
  latest_report?: {
    overall_compliant: boolean;
    actual_uptime_pct: number;
    actual_latency_p95_ms: number;
    actual_success_rate_pct: number;
    actual_cost_usd: number;
  };
}

export const MonitoringDashboard: React.FC<MonitoringDashboardProps> = ({ tenantId }) => {
  const [activeTab, setActiveTab] = useState<'health' | 'alerts' | 'sla' | 'costs'>('health');
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [alertRules, setAlertRules] = useState<AlertRule[]>([]);
  const [slaTargets, setSLATargets] = useState<SLATarget[]>([]);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [newEmail, setNewEmail] = useState('');

  // Fetch health status
  const fetchHealth = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/monitoring/health`);
      setHealth(response.data);
    } catch (error) {
      console.error('Failed to fetch health:', error);
    }
  };

  // Fetch alerts
  const fetchAlerts = async () => {
    try {
      const params: any = { limit: 50 };
      if (tenantId) params.tenantId = tenantId;
      const response = await axios.get(`${API_BASE}/api/monitoring/alerts`, { params });
      setAlerts(response.data.alerts);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  // Fetch alert rules
  const fetchAlertRules = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/alert-rules`);
      setAlertRules(response.data.rules || []);
    } catch (error) {
      console.error('Failed to fetch alert rules:', error);
    }
  };

  // Fetch SLA targets
  const fetchSLATargets = async () => {
    try {
      const params: any = {};
      if (tenantId) params.tenantId = tenantId;
      const response = await axios.get(`${API_BASE}/api/monitoring/sla`, { params });
      setSLATargets(response.data.sla_targets);
    } catch (error) {
      console.error('Failed to fetch SLA targets:', error);
    }
  };

  // Add email to alert rule
  const addEmailToRule = async (ruleId: number) => {
    if (!newEmail || !newEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      await axios.post(`${API_BASE}/api/admin/alert-rules/${ruleId}/add-email`, {
        email: newEmail
      });
      setNewEmail('');
      setEditingRule(null);
      fetchAlertRules();
    } catch (error) {
      console.error('Failed to add email:', error);
      alert('Failed to add email. Please try again.');
    }
  };

  // Remove email from alert rule
  const removeEmailFromRule = async (ruleId: number, email: string) => {
    try {
      await axios.post(`${API_BASE}/api/admin/alert-rules/${ruleId}/remove-email`, { email });
      fetchAlertRules();
    } catch (error) {
      console.error('Failed to remove email:', error);
    }
  };

  // Acknowledge alert
  const acknowledgeAlert = async (alertId: number) => {
    try {
      await axios.post(`${API_BASE}/api/monitoring/alerts/${alertId}/acknowledge`, {
        acknowledgedBy: 'admin',
        resolutionNote: 'Acknowledged from monitoring dashboard'
      });
      fetchAlerts();
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    }
  };

  useEffect(() => {
    fetchHealth();
    fetchAlerts();
    fetchAlertRules();
    fetchSLATargets();

    // Refresh data every 30 seconds
    const interval = setInterval(() => {
      fetchHealth();
      fetchAlerts();
    }, 30000);

    return () => clearInterval(interval);
  }, [tenantId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'degraded': return 'text-orange-600';
      case 'critical': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'info': return 'bg-blue-100 text-blue-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'error': return 'bg-orange-100 text-orange-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">📊 Monitoring Dashboard</h1>
        <p className="text-gray-600 mt-1">Real-time system monitoring and observability</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex space-x-4">
          {[
            { id: 'health', label: '💚 Health' },
            { id: 'alerts', label: '🚨 Alerts' },
            { id: 'sla', label: '📈 SLA' },
            { id: 'costs', label: '💰 Costs' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Health Tab */}
      {activeTab === 'health' && health && (
        <div className="space-y-6">
          {/* System Health Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">System Health</h2>
              <span className={`text-2xl font-bold ${getStatusColor(health.status)}`}>
                {health.status.toUpperCase()}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded">
                <div className="text-3xl font-bold text-blue-600">{health.metrics.total_executions}</div>
                <div className="text-sm text-gray-600 mt-1">Total Executions</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded">
                <div className="text-3xl font-bold text-green-600">
                  {health.metrics.success_rate_pct.toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600 mt-1">Success Rate</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded">
                <div className="text-3xl font-bold text-purple-600">
                  {health.metrics.avg_latency_ms.toFixed(0)}ms
                </div>
                <div className="text-sm text-gray-600 mt-1">Avg Latency</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded">
                <div className="text-3xl font-bold text-orange-600">
                  ${health.metrics.total_cost_usd.toFixed(4)}
                </div>
                <div className="text-sm text-gray-600 mt-1">Total Cost</div>
              </div>
            </div>
          </div>

          {/* Agent Performance */}
          {health.agents.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Agent Performance</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Executions</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success Rate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Latency</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {health.agents.map((agent) => (
                      <tr key={agent.agent_id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {agent.agent_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {agent.executions}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {agent.success_rate_pct.toFixed(1)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {agent.avg_latency_ms.toFixed(0)}ms
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          ${agent.total_cost_usd.toFixed(4)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-6">
          {/* Alert Rules Management */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">⚙️ Alert Rules & Email Recipients</h2>
            <div className="space-y-4">
              {alertRules.map((rule) => (
                <div key={rule.rule_id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-800">{rule.rule_name}</h3>
                        <span className={`px-2 py-1 rounded text-xs ${getSeverityColor(rule.severity)}`}>
                          {rule.severity}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          rule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {rule.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {rule.metric_type} {rule.threshold_operator} {rule.threshold_value}
                      </div>
                    </div>
                  </div>

                  {/* Email Recipients */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">📧 Email Recipients:</span>
                      <button
                        onClick={() => setEditingRule(editingRule?.rule_id === rule.rule_id ? null : rule)}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        {editingRule?.rule_id === rule.rule_id ? 'Cancel' : '+ Add Email'}
                      </button>
                    </div>

                    {/* Current Recipients */}
                    <div className="flex flex-wrap gap-2">
                      {rule.email_recipients?.map((email) => (
                        <div key={email} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm">
                          <span>{email}</span>
                          <button
                            onClick={() => removeEmailFromRule(rule.rule_id, email)}
                            className="ml-1 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Add Email Form */}
                    {editingRule?.rule_id === rule.rule_id && (
                      <div className="mt-2 flex gap-2">
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder="email@example.com"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        <button
                          onClick={() => addEmailToRule(rule.rule_id)}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                        >
                          Add
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Active Alerts */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">🚨 Active Alerts</h2>
            {alerts.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No active alerts. System is healthy! ✅</p>
            ) : (
              <div className="space-y-3">
                {alerts.filter(a => !a.resolved_at).map((alert) => (
                  <div key={alert.alert_id} className="border-l-4 border-red-500 bg-red-50 p-4 rounded">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-800">{alert.rule_name}</h3>
                          <span className={`px-2 py-1 rounded text-xs ${getSeverityColor(alert.severity)}`}>
                            {alert.severity}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">{alert.message}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          Triggered: {new Date(alert.triggered_at).toLocaleString()}
                        </p>
                      </div>
                      <button
                        onClick={() => acknowledgeAlert(alert.alert_id)}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      >
                        Acknowledge
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* SLA Tab */}
      {activeTab === 'sla' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">📈 SLA Compliance</h2>
          {slaTargets.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No SLA targets configured</p>
          ) : (
            <div className="space-y-4">
              {slaTargets.map((sla) => (
                <div key={sla.sla_id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-800">{sla.sla_name}</h3>
                      {sla.agent_id && (
                        <p className="text-sm text-gray-600">Agent: {sla.agent_id}</p>
                      )}
                    </div>
                    {sla.latest_report && (
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        sla.latest_report.overall_compliant
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {sla.latest_report.overall_compliant ? '✓ Compliant' : '✗ Non-Compliant'}
                      </span>
                    )}
                  </div>

                  {/* SLA Targets */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    {sla.uptime_percentage && (
                      <div className="bg-gray-50 p-3 rounded">
                        <div className="text-gray-600">Uptime Target</div>
                        <div className="font-semibold">{sla.uptime_percentage}%</div>
                        {sla.latest_report && (
                          <div className="text-xs text-gray-500 mt-1">
                            Actual: {sla.latest_report.actual_uptime_pct}%
                          </div>
                        )}
                      </div>
                    )}
                    {sla.max_latency_p95_ms && (
                      <div className="bg-gray-50 p-3 rounded">
                        <div className="text-gray-600">Max P95 Latency</div>
                        <div className="font-semibold">{sla.max_latency_p95_ms}ms</div>
                        {sla.latest_report && (
                          <div className="text-xs text-gray-500 mt-1">
                            Actual: {sla.latest_report.actual_latency_p95_ms}ms
                          </div>
                        )}
                      </div>
                    )}
                    {sla.min_success_rate_pct && (
                      <div className="bg-gray-50 p-3 rounded">
                        <div className="text-gray-600">Min Success Rate</div>
                        <div className="font-semibold">{sla.min_success_rate_pct}%</div>
                        {sla.latest_report && (
                          <div className="text-xs text-gray-500 mt-1">
                            Actual: {sla.latest_report.actual_success_rate_pct}%
                          </div>
                        )}
                      </div>
                    )}
                    {sla.max_cost_per_day_usd && (
                      <div className="bg-gray-50 p-3 rounded">
                        <div className="text-gray-600">Max Daily Cost</div>
                        <div className="font-semibold">${sla.max_cost_per_day_usd}</div>
                        {sla.latest_report && (
                          <div className="text-xs text-gray-500 mt-1">
                            Actual: ${sla.latest_report.actual_cost_usd.toFixed(2)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Costs Tab */}
      {activeTab === 'costs' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">💰 Cost Analytics</h2>
          <p className="text-gray-600">Cost analytics dashboard coming soon...</p>
          <p className="text-sm text-gray-500 mt-2">
            This will include cost trends, forecasts, budget tracking, and cost breakdown by agent.
          </p>
        </div>
      )}
    </div>
  );
};

export default MonitoringDashboard;
